import createPersistedState from 'vuex-persistedstate'
import dayjs from '@shared/services/DayJS'
const uuidv7 = require('uuidv7')

export default {
  state: {
    appVersion: null,
    authToken: null,
    currentCoreLocation: null,
    currentCoreLocationName: null,
    currentCoreLocationObj: null,
    currentLocation: null,
    currentLocationDispensary: null,
    currentLocationWholesale: null,
    currentLocationContext: 'dispensary', // Track current context
    currentSite: null,
    dateRange: {
      startDate: dayjs().subtract(7, 'days').toDate(),
      endDate: dayjs().toDate()
    },
    isImpersonating: false,
    loginRedirect: null,
    pkce: null,
    sessionId: uuidv7, // Unique session ID for each user session
    sidebarMinimize: false,
    sidebarShow: 'responsive',
    sidebarUnfoldable: false,
    sidebarVisible: '',
    // for subheader that shows when a form is dirty
    theDirtyDetails: {
      formChangeData: {},
      isDirty: false,
      maskedFormDataKeys: [], //maskedFormDataKeys: ['ispAccountPin','voipAutoAttendantPin','username','passPhrase'],
    },
    uiSettingsDatagridHeight: '700',
    uiSettingsDatagridTextSize: 'md',
    uiThemeMode: 'light',
    username: null,
    userProfile: null,
  },
  actions: {
    async generatePkce({ commit }) {
      // Helper functions defined within the action
      function generateCodeVerifier() {
        const array = new Uint32Array(32);
        window.crypto.getRandomValues(array);
        return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
      }

      async function generateCodeChallenge(verifier) {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return btoa(String.fromCharCode(...new Uint8Array(digest)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      }

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      commit('pkce', {
        codeVerifier,
        codeChallenge
      });
    },
  },
  getters: {
    appVersion(state) {
      return state.appVersion
    },
    authToken(state) {
      return state.authToken
    },
    currentCoreLocation(state) {
      return state.currentCoreLocation
    },
    currentCoreLocationName(state) {
      return state.currentCoreLocationName
    },
    currentCoreLocationObj(state) {
      return state.currentCoreLocationObj
    },
    currentLocation(state) {
      // Intelligently return the appropriate location based on current context
      if (state.currentLocationContext === 'wholesale') {
        return state.currentLocationWholesale || state.currentLocation
      }
      return state.currentLocationDispensary || state.currentLocation
    },
    currentLocationDispensary(state) {
      return state.currentLocationDispensary
    },
    currentLocationWholesale(state) {
      return state.currentLocationWholesale
    },
    currentLocationContext(state) {
      return state.currentLocationContext
    },
    currentSite(state) {
      return state.currentSite
    },
    dateRange(state) {
      return state.dateRange
    },
    isImpersonating(state) {
      return state.isImpersonating
    },
    loginRedirect(state) {
      return state.loginRedirect
    },
    pkce(state) {
      return state.pkce
    },
    sessionId(state) {
      return state.sessionId
    },
    theDirtyDetails(state) {
      return state.theDirtyDetails
    },
    uiSettingsDatagridHeight(state) {
      return state.uiSettingsDatagridHeight
    },
    uiSettingsDatagridTextSize(state) {
      return state.uiSettingsDatagridTextSize
    },
    uiThemeMode(state) {
      return state.uiThemeMode
    },
    userProfile(state) {
      return state.userProfile
    },
    username(state) {
      return state.username
    },
  },
  mutations: {
    appVersion(state, value) {
      state.appVersion = value
    },
    authToken(state, value) {
      state.authToken = value
    },
    currentCoreLocation(state, value) {
      state.currentCoreLocation = value
    },
    currentCoreLocationName(state, value) {
      state.currentCoreLocationName = value
    },
    currentCoreLocationObj(state, value) {
      state.currentCoreLocationObj = value
    },
    currentLocation(state, value) {
      // Intelligently route to the appropriate location based on current context
      state.currentLocation = value // Keep for backward compatibility
      if (state.currentLocationContext === 'wholesale') {
        state.currentLocationWholesale = value
      } else {
        state.currentLocationDispensary = value
      }
    },
    currentLocationDispensary(state, value) {
      state.currentLocationDispensary = value
    },
    currentLocationWholesale(state, value) {
      state.currentLocationWholesale = value
    },
    currentLocationContext(state, value) {
      state.currentLocationContext = value
    },
    dateRange(state, value) {
      state.dateRange = value
    },
    isImpersonating(state, value) {
      state.isImpersonating = value
    },
    loginRedirect(state, value) {
      state.loginRedirect = value
    },
    pkce(state, value) {
      state.pkce = value
    },
    reset(state) {
      state.appVersion = null
      state.authToken = null
      state.currentCoreLocation = null
      state.currentCoreLocationName = null
      state.currentCoreLocationObj = null
      state.currentLocation = null
      state.currentLocationDispensary = null
      state.currentLocationWholesale = null
      state.currentLocationContext = 'dispensary'
      state.isImpersonating = false
      state.pkce = null
      state.theDirtyDetails = {
        formChangeData: {},
        isDirty: false,
        maskedFormDataKeys: [], //maskedFormDataKeys: ['ispAccountPin','voipAutoAttendantPin','username','passPhrase'],
      }
      state.userProfile = null
      state.uiSettingsDatagridHeight = '700'
      state.uiSettingsDatagridTextSize = 'md'
      state.uiThemeMode = 'light'
      state.username = null
    },
    sessionId(state, value) {
      state.sessionId = value
    },
    set(state, [variable, value]) {
      state[variable] = value
    },
    theDirtyDetails(state, value) {
      state.theDirtyDetails = value
    },
    toggleSidebarDesktop(state) {
      const sidebarOpened = [true, 'responsive'].includes(state.sidebarShow)
      state.sidebarShow = sidebarOpened ? false : 'responsive'
    },
    toggleSidebarMobile(state) {
      const sidebarClosed = [false, 'responsive'].includes(state.sidebarShow)
      state.sidebarShow = sidebarClosed ? true : 'responsive'
    },
    uiSettingsDatagridHeight(state, value) {
      state.uiSettingsDatagridHeight = value
    },
    uiSettingsDatagridTextSize(state, value) {
      state.uiSettingsDatagridTextSize = value
    },
    uiThemeMode(state, value) {
      state.uiThemeMode = value
    },
    userProfile(state, value) {
      state.userProfile = value
    },
    username(state, value) {
      state.username = value
    },
  },
  modules: {},
  plugins: [
    createPersistedState({
      key: 'vuex-portal',
    }),
  ],
}
